/**
 * FavoritesService - Device definition favorites management
 * 
 * Uses ProfileSettingsBackend for storage:
 * - Logged OUT: localStorage
 * - Logged IN: profiles.settings.favorites in Supabase
 * 
 * On login: migrate localStorage → cloud, clear localStorage
 */

import { FAVORITES_STORAGE_KEY } from '../constants/index.js';
import { createProfileSettingsBackend } from './backends/ProfileSettingsBackend.js';

const CLOUD_KEY = 'favorites';  // Key in profiles.settings

export function createFavoritesService() {
    let favorites = {};
    let authService = null;
    let backend = null;
    let syncTimeout = null;
    const SYNC_DEBOUNCE_MS = 2000;

    function setAuthService(auth) {
        authService = auth;
        backend = createProfileSettingsBackend(auth);
    }

    /**
     * Load favorites from localStorage (for logged-out users)
     */
    async function load() {
        if (backend?.isLoggedIn()) {
            console.log('[FAV] load() - logged in, skipping (use loadFromCloud)');
            return;
        }
        
        console.log('[FAV] load() - loading from localStorage');
        const saved = backend?.getLocal(FAVORITES_STORAGE_KEY);
        
        // Handle old array format or null
        if (!saved || Array.isArray(saved)) {
            favorites = {};
        } else {
            favorites = saved;
        }
        
        console.log('[FAV] load() - loaded', Object.keys(favorites).length, 'favorites');
    }

    /**
     * Save favorites to current backend
     */
    function save() {
        if (!backend) return false;
        
        if (backend.isLoggedIn()) {
            // Debounced cloud save
            if (syncTimeout) clearTimeout(syncTimeout);
            syncTimeout = setTimeout(async () => {
                console.log('[FAV] save() - pushing', Object.keys(favorites).length, 'to cloud');
                await backend.set(CLOUD_KEY, favorites);
            }, SYNC_DEBOUNCE_MS);
        } else {
            // Immediate localStorage save
            console.log('[FAV] save() - saving', Object.keys(favorites).length, 'to localStorage');
            backend.setLocal(FAVORITES_STORAGE_KEY, favorites);
        }
        
        return true;
    }

    /**
     * Login migration: merge localStorage → cloud, clear localStorage
     */
    async function loadFromCloud() {
        console.log('[FAV] ========== loadFromCloud() START ==========');
        
        if (!backend?.isLoggedIn()) {
            console.log('[FAV] loadFromCloud() - ABORTING: not logged in');
            return;
        }

        // Step 1: Read both sources
        const localData = backend.getLocal(FAVORITES_STORAGE_KEY) || {};
        const cloudData = await backend.get(CLOUD_KEY) || {};
        
        // Handle old array format
        const localFavorites = Array.isArray(localData) ? {} : localData;
        
        const localKeys = Object.keys(localFavorites);
        const cloudKeys = Object.keys(cloudData);
        
        console.log('[FAV] loadFromCloud() - LOCAL:', localKeys.length, 'items');
        console.log('[FAV] loadFromCloud() - CLOUD:', cloudKeys.length, 'items');

        // Step 2: Merge (local + cloud, cloud wins on conflict)
        favorites = { ...localFavorites, ...cloudData };
        
        const mergedKeys = Object.keys(favorites);
        const migratedKeys = localKeys.filter(k => !cloudKeys.includes(k));
        
        console.log('[FAV] loadFromCloud() - MERGED:', mergedKeys.length, 'items');

        // Step 3: Push merged to cloud (if we had local data to migrate)
        if (migratedKeys.length > 0) {
            console.log('[FAV] loadFromCloud() - Migrating', migratedKeys.length, 'from local');
            await backend.set(CLOUD_KEY, favorites);
        }

        // Step 4: Clear localStorage
        if (localKeys.length > 0) {
            console.log('[FAV] loadFromCloud() - Clearing localStorage');
            backend.removeLocal(FAVORITES_STORAGE_KEY);
        }

        console.log('[FAV] ========== loadFromCloud() COMPLETE ==========');
    }

    // ============= FAVORITES API =============

    function isFavorite(templateId) {
        return favorites.hasOwnProperty(templateId);
    }

    function add(templateId, template) {
        if (!template) return false;
        const cloned = JSON.parse(JSON.stringify(template));
        if ('isRouter' in template) {
            console.log(`[ISROUTER] FavoritesService.add: "${template.name}" isRouter=${template.isRouter}`);
        }
        favorites[templateId] = cloned;
        console.log('[FAV] add -', templateId, '| total:', Object.keys(favorites).length);
        return save();
    }

    function remove(templateId) {
        if (favorites.hasOwnProperty(templateId)) {
            delete favorites[templateId];
            console.log('[FAV] remove -', templateId, '| total:', Object.keys(favorites).length);
            return save();
        }
        return true;
    }

    function toggle(templateId, template) {
        if (favorites.hasOwnProperty(templateId)) {
            delete favorites[templateId];
            console.log('[FAV] toggle - removed', templateId);
            return { isFavorite: false, success: save() };
        } else if (template) {
            favorites[templateId] = JSON.parse(JSON.stringify(template));
            console.log('[FAV] toggle - added', templateId);
            return { isFavorite: true, success: save() };
        }
        return { isFavorite: false, success: false };
    }

    function getAll() {
        return { ...favorites };
    }

    function getAllAsArray() {
        return Object.values(favorites);
    }

    function get(templateId) {
        return favorites[templateId] || null;
    }

    function getCount() {
        return Object.keys(favorites).length;
    }

    function clear() {
        favorites = {};
        return save();
    }

    /**
     * Debug function - call from console
     */
    async function debug() {
        console.log('========== FAVORITES DEBUG ==========');
        console.log('1. Auth state:');
        console.log('   - isLoggedIn:', backend?.isLoggedIn());
        
        console.log('\n2. In-memory:', Object.keys(favorites).length, 'favorites');
        console.log('   - IDs:', Object.keys(favorites));
        
        console.log('\n3. localStorage:');
        const local = backend?.getLocal(FAVORITES_STORAGE_KEY);
        console.log('   - count:', local ? Object.keys(local).length : 0);
        
        console.log('\n4. Cloud:');
        if (backend?.isLoggedIn()) {
            const cloud = await backend.get(CLOUD_KEY);
            console.log('   - count:', cloud ? Object.keys(cloud).length : 0);
        } else {
            console.log('   - skipped (not logged in)');
        }
        console.log('======================================');
    }

    return {
        setAuthService,
        load,
        save,
        loadFromCloud,
        isFavorite,
        add,
        remove,
        toggle,
        getAll,
        getAllAsArray,
        get,
        getCount,
        clear,
        debug
    };
}
